import {
  bible_highlight,
  bible_verse_highlight,
  eq,
  inArray,
  type PowerSyncDrizzleDB,
} from "@bso/powersync";
import {
  buildImportReport,
  coordKey,
  type ExistingState,
  type ImportReport,
  LEGACY_IMPORT_GROUP_COLOR,
  LEGACY_IMPORT_ORIGIN,
  type LegacyImportPlan,
  resolveImport,
} from "@bso/shared";

/**
 * Writing legacy migration plan into LOCAL database (PowerSync).
 *
 * Everything passes through the same local tables used by the rest of the app, so:
 *   - works **offline** and **without account** (guest), like the rest of the product;
 *   - server upload is handled by standard CRUD queue (no new endpoint);
 *   - `created_at` travels with each row and Rust write-path preserves it.
 *
 * Migration NEVER deletes or overwrites: only INSERTS rows that did not
 * exist. See `docs/LEGACY_MIGRATION.md`.
 */

/** Reading position key persisted by reader. */
const LAST_READ_KEY = "last-read-bible";

/** Canonical abbreviations by book number, for reading position. */
function hasLastRead(): boolean {
  try {
    return localStorage.getItem(LAST_READ_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Reads state resolving the plan: which ids already exist and what
 * coordinates user already has occupied with LIVE highlights.
 */
async function readExistingState(
  db: PowerSyncDrizzleDB,
  userId: string,
  plan: LegacyImportPlan,
): Promise<ExistingState> {
  const candidates = plan.verseHighlights.map((row) => row.id);
  const ids = new Set<string>();

  // Queries ONLY plan ids: cost scales with migration size,
  // not with user history.
  if (candidates.length > 0) {
    const rows = await db
      .select({ id: bible_verse_highlight.id })
      .from(bible_verse_highlight)
      .where(inArray(bible_verse_highlight.id, candidates));
    for (const row of rows) {
      if (row.id) {
        ids.add(row.id);
      }
    }
  }

  const [live, group] = await Promise.all([
    db
      .select({
        version: bible_verse_highlight.version,
        book: bible_verse_highlight.book,
        chapter: bible_verse_highlight.chapter,
        verse: bible_verse_highlight.verse,
      })
      .from(bible_verse_highlight)
      .where(eq(bible_verse_highlight.user_id, userId)),
    db
      .select({ id: bible_highlight.id })
      .from(bible_highlight)
      .where(eq(bible_highlight.id, LEGACY_IMPORT_ORIGIN)),
  ]);

  return {
    ids,
    highlightedCoords: new Set(
      live.map((row) =>
        coordKey({
          version: row.version ?? "",
          book: row.book ?? 0,
          chapter: row.chapter ?? 0,
          verse: row.verse ?? 0,
        }),
      ),
    ),
    groupExists: group.length > 0,
    hasLastRead: hasLastRead(),
  };
}

/** Translates legacy reading position into key consumed by reader. */
function applyReadingPosition(chapter: number, bookAbbr: string | undefined): void {
  if (!bookAbbr) {
    return;
  }
  try {
    localStorage.setItem(
      LAST_READ_KEY,
      JSON.stringify({ version: "kjv", bookAbbr, chapter: String(chapter || 1) }),
    );
  } catch {
    /* storage unavailable: position is an extra, never a blocker */
  }
}

/**
 * Aplica el plan y devuelve el informe.
 *
 * @param abbrForBook Resolves legacy book number (1..66) to its canonical
 *   abbreviation. Injected so module does not depend on route catalog.
 */
export async function applyImportPlan(
  db: PowerSyncDrizzleDB,
  userId: string,
  plan: LegacyImportPlan,
  abbrForBook?: (book: number) => string | undefined,
): Promise<ImportReport> {
  const existing = await readExistingState(db, userId, plan);
  const resolved = resolveImport(plan, existing);
  const now = new Date().toISOString();

  // Group ALWAYS precedes its verses: server replicates CRUD queue
  // in order and validates parent->child relationship.
  if (resolved.createGroup) {
    await db.insert(bible_highlight).values({
      id: resolved.groupId,
      user_id: userId,
      color: LEGACY_IMPORT_GROUP_COLOR,
      created_at: now,
      updated_at: now,
    });
  }

  for (const row of resolved.verseHighlights) {
    // Sequential by design: preserves deterministic order of CRUD queue.
    // oxlint-disable-next-line no-await-in-loop
    await db.insert(bible_verse_highlight).values({
      id: row.id,
      user_id: userId,
      version: row.version,
      book: row.book,
      chapter: row.chapter,
      verse: row.verse,
      color: row.color,
      // Fixed `origin_id` = migration marker: excludes these rows from streak.
      origin_id: resolved.groupId,
      created_at: now,
      updated_at: now,
    });
  }

  const position = resolved.appliedPreferences;
  if (position?.currentBook !== undefined) {
    applyReadingPosition(position.currentChapter ?? 1, abbrForBook?.(position.currentBook));
  }

  return buildImportReport(plan, resolved);
}
